import type { ComponentProps } from "react";
import { Link } from "react-router-dom";
import { t } from "ttag";

import { TESTOSTERONE_STEP_ICONS } from "@/features/testosterone-therapy/utils/icons";
import type {
  TestosteroneStep,
  TestosteroneStepAction,
} from "@/shared/domains";
import { TESTOSTERONE_STEP_ACTION_TYPES } from "@/shared/domains";
import {
  IS_WEB_VIEW,
  sendMessageToReactNativeWebView,
  WEB_VIEW_ACTIONS,
} from "@/shared/lib";
import { Button, Dialog } from "@/shared/ui";

const getTexts = (type: TestosteroneStepAction["type"]) => {
  switch (type) {
    case TESTOSTERONE_STEP_ACTION_TYPES.SCHEDULE_LAB:
      return {
        title: t`Before you schedule`,
        description: t`You'll be redirected to our lab partner's site to book your appointment. You only need to do this once — after that, just wait for your results. We'll notify you when they're ready so you can continue with your treatment!`,
        buttonLabel: t`Continue to booking`,
      };

    default:
      return {
        title: t`You're being redirected`,
        description: t`You'll be redirected to an external site to complete the next step. You can return here once you're finished.`,
        buttonLabel: t`Continue to external site`,
      };
  }
};

type ExternalActionConfirmModalProps = ComponentProps<typeof Dialog.Root> & {
  stepType: TestosteroneStep["type"];
  action: TestosteroneStepAction;
};

export const ExternalActionConfirmModal = ({
  stepType,
  action,
  open,
  ...props
}: ExternalActionConfirmModalProps) => {
  const { title, description, buttonLabel } = getTexts(action.type);

  return (
    <Dialog.Root open={open} {...props}>
      <Dialog.Content>
        <Dialog.Header icon={TESTOSTERONE_STEP_ICONS[stepType]}>
          <Dialog.Title>{title}</Dialog.Title>

          <Dialog.Description>{description}</Dialog.Description>
        </Dialog.Header>

        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button size="lg" variant="secondary">
              {t`Back`}
            </Button>
          </Dialog.Close>

          {IS_WEB_VIEW ? (
            <Button
              size="lg"
              onClick={() => {
                sendMessageToReactNativeWebView({
                  type: WEB_VIEW_ACTIONS.GO_TO_EXTERNAL_URL,
                  payload: {
                    url: action.url,
                  },
                });
              }}
            >
              {buttonLabel}
            </Button>
          ) : (
            <Button asChild size="lg">
              <Link to={action.url} target="_blank">
                {buttonLabel}
              </Link>
            </Button>
          )}
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};
